import React, { useState } from 'react';
import { Table, Button, Space, message, Modal, Tag } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import apiClient from '../../../api/axios';

interface AdminOlePageListDto {
    id: string;
    title: string;
    slug: string;
    isActive: boolean;
    availableLanguages: string[];
}

export const OlePagesList: React.FC = () => {
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const queryClient = useQueryClient();
    const navigate = useNavigate();

    const { data, isLoading } = useQuery({
        queryKey: ['adminOlePages', page, pageSize],
        queryFn: async () => {
            const response = await apiClient.get('/admin/ole/pages', {
                params: { page, pageSize }
            });
            return response.data;
        }
    });

    const deleteMutation = useMutation({
        mutationFn: async (id: string) => {
            await apiClient.delete(`/admin/ole/pages/${id}`);
        },
        onSuccess: () => {
            message.success('Sayfa başarıyla silindi.');
            queryClient.invalidateQueries({ queryKey: ['adminOlePages'] });
        },
        onError: () => {
            message.error('Sayfa silinirken hata oluştu.');
        }
    });

    const handleDelete = (id: string) => {
        Modal.confirm({
            title: 'Bu sayfayı silmek istediğinize emin misiniz?',
            content: 'Bu işlem geri alınamaz.',
            okText: 'Evet, Sil',
            okType: 'danger',
            cancelText: 'İptal',
            onOk: () => deleteMutation.mutate(id)
        });
    };

    const columns = [
        { title: 'Sayfa Adı (Ana)', dataIndex: 'title', key: 'title' },
        { title: 'Slug (URL)', dataIndex: 'slug', key: 'slug' },
        {
            title: 'Durum',
            dataIndex: 'isActive',
            key: 'isActive',
            render: (isActive: boolean) => (
                <Tag color={isActive ? 'green' : 'red'}>{isActive ? 'Aktif' : 'Pasif'}</Tag>
            )
        },
        {
            title: 'Çeviriler',
            dataIndex: 'availableLanguages',
            key: 'availableLanguages',
            render: (langs: string[]) => (
                <Space size={[0, 8]} wrap>
                    {langs?.map(l => <Tag key={l}>{l.toUpperCase()}</Tag>)}
                </Space>
            )
        },
        {
            title: 'İşlemler',
            key: 'actions',
            render: (_: any, record: AdminOlePageListDto) => (
                <Space size="middle">
                    <Button icon={<EditOutlined />} onClick={() => navigate(`/admin/ole/pages/edit/${record.id}`)} />
                    <Button danger icon={<DeleteOutlined />} onClick={() => handleDelete(record.id)} />
                </Space>
            )
        }
    ];

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
                <h2>OLE Sayfaları</h2>
                <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/admin/ole/pages/create')}>
                    Yeni Sayfa Ekle
                </Button>
            </div>

            <Table
                dataSource={data?.data || []}
                columns={columns}
                rowKey="id"
                loading={isLoading}
                pagination={{
                    current: page,
                    pageSize: pageSize,
                    total: data?.totalRecords || 0,
                    onChange: (p, s) => { setPage(p); setPageSize(s); }
                }}
            />
        </div>
    );
};
